import type { Tone } from '../../../components/analytics/reportTheme'
import type {
  RiskClassification,
  SupplierRiskItem,
  SupplierRiskPenalty,
} from '../../../api/models/response/supplierRisk'

/**
 * Textos y tonos del Ranking de Riesgo de Proveedores. Todo lo que el usuario lee
 * sobre una regla sale de aquí, en lenguaje llano: qué mide, por qué importa y qué
 * hacer para levantarla. El backend manda el dato concreto (`detail`).
 */

export const CLASS_TONE: Record<RiskClassification, Tone> = { Low: 'ok', Medium: 'warn', High: 'bad' }

export const CLASS_LABEL: Record<RiskClassification, string> = { Low: 'Bajo', Medium: 'Medio', High: 'Alto' }

/** Rango del baremo (req. 2.3.3). */
export const CLASS_RANGE: Record<RiskClassification, string> = { Low: '0–20', Medium: '21–50', High: '51–100' }

/**
 * Acción que pide el baremo. El bloqueo automático en garita/balanza es la fase 2 y
 * todavía no existe: no se promete un "bloqueo inmediato" que nadie ejecuta.
 */
export const CLASS_ACTION: Record<RiskClassification, string> = {
  Low: 'Aprobación automática',
  Medium: 'Debida diligencia reforzada (visado manual)',
  High: 'Requiere bloqueo de guías y pesaje',
}

export const CLASS_ORDER: RiskClassification[] = ['High', 'Medium', 'Low']

interface RuleCopy {
  /** Título corto para la tabla (motivo principal). */
  short: string
  /** Variante corta cuando la regla se dispara por falta de dato y no por un mal estado. */
  shortMissing?: string
  /** Por qué importa, en una frase. */
  why: string
  /** Qué hacer para levantarla. */
  fix?: string
}

export const RULE_COPY: Record<string, RuleCopy> = {
  RUC_FICHA_VIGENCIA: {
    short: 'Ficha RUC desactualizada',
    why: 'La ficha RUC debe consultarse en SUNAT al menos cada 30 días para confiar en su estado.',
    fix: 'Vuelve a consultar el RUC desde la ficha del proveedor.',
  },
  REPORTE_TRIBUTARIO_VIGENCIA: {
    short: 'Reporte tributario vencido',
    why: 'El reporte tributario de SUNAT vale 10 días desde su emisión.',
    fix: 'Carga un reporte tributario emitido en los últimos 10 días.',
  },
  RUC_ESTADO: {
    short: 'RUC no activo',
    shortMissing: 'Sin estado SUNAT',
    why: 'Con el RUC de baja o suspendido el proveedor no puede emitir comprobantes: no se le puede comprar.',
    fix: 'Confirma el estado en SUNAT; si sigue sin estar activo, no programes compras.',
  },
  RUC_CONDICION: {
    short: 'Domicilio no habido',
    why: 'Un contribuyente NO HABIDO no da derecho a crédito fiscal y es señal de alerta tributaria.',
    fix: 'Pide al proveedor que regularice su domicilio fiscal en SUNAT.',
  },
  REINFO_ESTADO: {
    short: 'REINFO no vigente',
    shortMissing: 'Sin estado REINFO',
    why: 'Solo un minero con REINFO vigente (o ya formalizado) puede comercializar mineral.',
    fix: 'Verifica el estado en el REINFO; si está suspendido o excluido, no recibas su mineral.',
  },
  IGAFOM_OMISION: {
    short: 'Sin IGAFOM vigente',
    why: 'Sin un IGAFOM vigente ningún derecho minero del proveedor pasa la validación de recepción.',
    fix: 'Registra el IGAFOM (documento y cargo) en sus derechos mineros, o renueva el vencido.',
  },
  IGAFOM_COBERTURA: {
    short: 'IGAFOM incompleto',
    why: 'Los derechos mineros sin IGAFOM vigente no podrán entregar mineral aunque los demás sí.',
    fix: 'Completa el IGAFOM de los derechos mineros que faltan.',
  },
  CIIU_COHERENCIA: {
    short: 'CIIU no autorizado',
    why: 'La actividad económica debe ser minera o de comercio de minerales (CIIU 0729, 4662 o 0899).',
  },
  TITULAR_COHERENCIA: {
    short: 'Titular no coincide',
    why: 'El titular del REINFO debería ser el representante legal de la empresa.',
  },
  UBO_OMISION: {
    short: 'Sin registro de UBO',
    why: 'Se exige identificar a los accionistas y beneficiarios finales del proveedor.',
  },
}

/**
 * Regla de estado (SUNAT / REINFO) disparada por falta de dato y no por un mal estado.
 * El backend la manda en ámbar; el estado malo va en rojo.
 */
export const isMissingVariant = (p: SupplierRiskPenalty) =>
  p.triggered && p.severity === 'Amber' && (p.code === 'RUC_ESTADO' || p.code === 'REINFO_ESTADO')

export function ruleShort(p: SupplierRiskPenalty): string {
  const copy = RULE_COPY[p.code]
  if (!copy) return p.label
  return isMissingVariant(p) && copy.shortMissing ? copy.shortMissing : copy.short
}

export function penaltyTone(p: SupplierRiskPenalty): Tone {
  if (!p.dataAvailable) return 'neutral'
  if (!p.triggered) return 'ok'
  return p.severity === 'Red' ? 'bad' : 'warn'
}

/** Reglas que sumaron, de mayor a menor peso (las críticas primero ante empate). */
export function triggeredPenalties(item: SupplierRiskItem): SupplierRiskPenalty[] {
  return item.penalties
    .filter((p) => p.triggered)
    .sort((a, b) => b.points - a.points || Number(b.critical) - Number(a.critical))
}

/** Tono de la columna de estado (SUNAT / REINFO) según la regla que la evalúa. */
export function statusTone(item: SupplierRiskItem, code: 'RUC_ESTADO' | 'REINFO_ESTADO'): Tone {
  const p = item.penalties.find((x) => x.code === code)
  if (!p) return 'neutral'
  if (!p.triggered) return 'ok'
  return isMissingVariant(p) ? 'neutral' : 'bad'
}

export const fmtInt = (n: number) => n.toLocaleString('es-PE')

export const fmtPct = (n: number) =>
  `${n.toLocaleString('es-PE', { maximumFractionDigits: 0 })} %`

/** 'yyyy-MM-dd' → 'dd/mm/aaaa' sin pasar por Date (evita el corrimiento de zona). */
export function fmtIsoDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const [y, m, d] = iso.slice(0, 10).split('-')
  return y && m && d ? `${d}/${m}/${y}` : '—'
}
